
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SubscriptionTier, UserSubscription, UserUsage } from '@/components/admin/types';
import { featureFlags } from '@/utils/featureFlags';

/**
 * Get all available subscription tiers
 */
export const getSubscriptionTiers = async (): Promise<SubscriptionTier[]> => {
  try {
    const { data, error } = await supabase
      .from('subscription_tiers')
      .select('*')
      .eq('is_active', true)
      .order('price');

    if (error) throw error;
    return data || [];
  } catch (error: any) {
    console.error('Error fetching subscription tiers:', error);
    toast.error('Failed to load subscription plans');
    return [];
  }
};

/**
 * Get the current user's subscription with enhanced billing period detection
 */
export const getUserSubscription = async (userId: string): Promise<UserSubscription & { billingPeriod?: 'monthly' | 'yearly' } | null> => {
  try {
    // First try to get from profiles table (much faster)
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('subscription_tier, subscription_status, subscription_end, stripe_customer_id')
      .eq('id', userId)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile subscription:', profileError);
    }

    if (profileData && profileData.subscription_tier) {
      // Enhanced billing period detection
      let billingPeriod: 'monthly' | 'yearly' | undefined;
      if (profileData.subscription_end) {
        const subEnd = new Date(profileData.subscription_end);
        const now = new Date();
        const diffDays = Math.ceil((subEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        // More accurate billing period detection:
        // If subscription ends more than 45 days from now, likely yearly
        // If subscription ends within 35 days, likely monthly
        if (diffDays > 45) {
          billingPeriod = 'yearly';
        } else if (diffDays <= 35) {
          billingPeriod = 'monthly';
        } else {
          // Ambiguous case - default to monthly
          billingPeriod = 'monthly';
        }
      }

      console.log('Detected billing period:', { 
        subscriptionEnd: profileData.subscription_end, 
        billingPeriod,
        tier: profileData.subscription_tier 
      });

      return {
        id: userId,
        user_id: userId,
        tier_id: profileData.subscription_tier,
        status: profileData.subscription_status as "active" | "canceled" | "past_due",
        current_period_start: new Date().toISOString(),
        current_period_end: profileData.subscription_end || new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        billingPeriod,
        tier: {
          id: profileData.subscription_tier,
          name: profileData.subscription_tier.charAt(0).toUpperCase() + profileData.subscription_tier.slice(1),
          description: `${profileData.subscription_tier} subscription`,
          price: profileData.subscription_tier === 'plus' ? 10 : profileData.subscription_tier === 'pro' ? 20 : 0,
          features: [],
          prompt_copy_limit: profileData.subscription_tier === 'plus' ? 999999 : profileData.subscription_tier === 'pro' ? 999999 : 100,
          is_active: true,
          created_at: new Date().toISOString()
        }
      };
    }

    // Fallback to old user_subscriptions table logic
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(`
        *,
        tier:tier_id(*)
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) throw error;
    
    if (data) {
      const validStatus = data.status as "active" | "canceled" | "past_due";
      const typedSubscription: UserSubscription & { billingPeriod?: 'monthly' | 'yearly' } = {
        ...data,
        status: validStatus,
        tier: data.tier as SubscriptionTier
      };
      
      return typedSubscription;
    }
    
    return null;
  } catch (error: any) {
    console.error('Error fetching user subscription:', error);
    return null;
  }
};

/**
 * Get AI usage limits based on subscription tier - now reads from profiles
 */
export const getAIUsageLimits = async (userId: string) => {
  try {
    // Get subscription info directly from profiles
    const { data: profileData } = await supabase
      .from('profiles')
      .select('subscription_tier, subscription_status')
      .eq('id', userId)
      .single();

    const tier = profileData?.subscription_tier || 'free';
    const isActive = profileData?.subscription_status === 'active';

    if (!isActive || tier === 'free') {
      return {
        chatMessagesLimit: 0,
        promptGenerationsLimit: 0,
        copilotMessagesLimit: 5, // Free tier gets 5 copilot messages
        hasProductCopilotAccess: true,
        promptCopyLimit: 100,
        promptSaveLimit: 100
      };
    }

    if (tier === 'plus') {
      return {
        chatMessagesLimit: 50,
        promptGenerationsLimit: 50,
        copilotMessagesLimit: 100,
        hasProductCopilotAccess: true,
        promptCopyLimit: 999999,
        promptSaveLimit: 999999
      };
    }
    
    if (tier === 'pro') {
      return {
        chatMessagesLimit: 100,
        promptGenerationsLimit: 100,
        copilotMessagesLimit: 300,
        hasProductCopilotAccess: true,
        promptCopyLimit: 999999,
        promptSaveLimit: 999999
      };
    }
    
    // Default to free tier
    return {
      chatMessagesLimit: 0,
      promptGenerationsLimit: 0,
      copilotMessagesLimit: 5,
      hasProductCopilotAccess: true,
      promptCopyLimit: 100,
      promptSaveLimit: 100
    };
  } catch (error) {
    console.error('Error getting usage limits:', error);
    // Return free tier limits on error
    return {
      chatMessagesLimit: 0,
      promptGenerationsLimit: 0,
      copilotMessagesLimit: 5,
      hasProductCopilotAccess: true,
      promptCopyLimit: 100,
      promptSaveLimit: 100
    };
  }
};

/**
 * Check if user has premium access to Product Copilot
 */
export const hasProductCopilotAccess = async (userId: string): Promise<boolean> => {
  try {
    if (!featureFlags.enableSubscriptionLimits) {
      return true;
    }
    
    const { data: isAdmin } = await supabase.rpc('is_admin');
    if (isAdmin) {
      return true;
    }
    
    if (!userId) {
      return false;
    }
    
    const limits = await getAIUsageLimits(userId);
    return limits.hasProductCopilotAccess;
  } catch (error: any) {
    console.error('Error checking Product Copilot access:', error);
    return false;
  }
};

/**
 * Get the current user's usage for the current billing period
 */
export const getUserUsage = async (userId: string): Promise<UserUsage | null> => {
  try {
    const limits = await getAIUsageLimits(userId);
    
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const { data, error } = await supabase
      .from('prompt_copies')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', monthStart.toISOString())
      .lte('created_at', monthEnd.toISOString());

    if (error) throw error;
    
    return {
      user_id: userId,
      prompt_copies_used: data ? data.length : 0,
      prompt_copies_limit: limits.promptCopyLimit,
      period_start: monthStart.toISOString(),
      period_end: monthEnd.toISOString()
    };
  } catch (error: any) {
    console.error('Error fetching user usage:', error);
    toast.error('Failed to load your usage information');
    return null;
  }
};

/**
 * Check if the user is an admin
 */
const isUserAdmin = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('is_admin');
    
    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Error in isUserAdmin:', error);
    return false;
  }
};

/**
 * Enhanced function to check if user can copy prompts - now uses profiles table
 */
export const canUserCopyPrompt = async (userId: string): Promise<{
  canCopy: boolean;
  usedCount: number;
  limit: number;
  percentUsed: number;
}> => {
  try {
    if (!featureFlags.enableSubscriptionLimits) {
      return { canCopy: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    if (!userId) {
      return { canCopy: false, usedCount: 0, limit: 100, percentUsed: 0 };
    }
    
    const admin = await isUserAdmin(userId);
    if (admin) {
      return { canCopy: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    const limits = await getAIUsageLimits(userId);
    
    // Get current month usage
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const { data, error } = await supabase
      .from('prompt_copies')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', monthStart.toISOString())
      .lte('created_at', monthEnd.toISOString());

    if (error) throw error;
    
    const usedCount = data ? data.length : 0;
    const limit = limits.promptCopyLimit;
    const canCopy = usedCount < limit;
    const percentUsed = limit > 0 ? Math.round((usedCount / limit) * 100) : 0;
    
    return {
      canCopy,
      usedCount,
      limit,
      percentUsed
    };
  } catch (error: any) {
    console.error('Error checking prompt copy limit:', error);
    return { canCopy: true, usedCount: 0, limit: 100, percentUsed: 0 };
  }
};

/**
 * Enhanced function to check AI chat message usage - now uses profiles table
 */
export const canUserSendChatMessage = async (userId: string): Promise<{
  canSend: boolean;
  usedCount: number;
  limit: number;
  percentUsed: number;
}> => {
  try {
    if (!featureFlags.enableSubscriptionLimits) {
      return { canSend: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    if (!userId) {
      return { canSend: false, usedCount: 0, limit: 0, percentUsed: 0 };
    }
    
    const admin = await isUserAdmin(userId);
    if (admin) {
      return { canSend: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    const limits = await getAIUsageLimits(userId);
    
    // Get current month usage from ai_chat_usage table
    const { data: periodData } = await supabase.rpc('get_current_billing_period');
    const currentPeriod = periodData?.[0];
    
    if (!currentPeriod) {
      return { canSend: true, usedCount: 0, limit: limits.chatMessagesLimit, percentUsed: 0 };
    }
    
    const { data: usageData, error } = await supabase
      .from('ai_chat_usage')
      .select('messages_used')
      .eq('user_id', userId)
      .eq('period_start', currentPeriod.period_start)
      .eq('period_end', currentPeriod.period_end)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching chat usage:', error);
    }
    
    const usedCount = usageData?.messages_used || 0;
    const limit = limits.chatMessagesLimit;
    const canSend = usedCount < limit;
    const percentUsed = limit > 0 ? Math.round((usedCount / limit) * 100) : 0;
    
    return {
      canSend,
      usedCount,
      limit,
      percentUsed
    };
  } catch (error: any) {
    console.error('Error checking chat message limit:', error);
    return { canSend: true, usedCount: 0, limit: 50, percentUsed: 0 };
  }
};

/**
 * Enhanced function to check AI prompt generation usage - now uses profiles table
 */
export const canUserGeneratePrompt = async (userId: string): Promise<{
  canGenerate: boolean;
  usedCount: number;
  limit: number;
  percentUsed: number;
}> => {
  try {
    if (!featureFlags.enableSubscriptionLimits) {
      return { canGenerate: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    if (!userId) {
      return { canGenerate: false, usedCount: 0, limit: 0, percentUsed: 0 };
    }
    
    const admin = await isUserAdmin(userId);
    if (admin) {
      return { canGenerate: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    const limits = await getAIUsageLimits(userId);
    
    // Get current month usage from ai_generation_usage table
    const { data: periodData } = await supabase.rpc('get_current_billing_period');
    const currentPeriod = periodData?.[0];
    
    if (!currentPeriod) {
      return { canGenerate: true, usedCount: 0, limit: limits.promptGenerationsLimit, percentUsed: 0 };
    }
    
    const { data: usageData, error } = await supabase
      .from('ai_generation_usage')
      .select('generations_used')
      .eq('user_id', userId)
      .eq('period_start', currentPeriod.period_start)
      .eq('period_end', currentPeriod.period_end)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching generation usage:', error);
    }
    
    const usedCount = usageData?.generations_used || 0;
    const limit = limits.promptGenerationsLimit;
    const canGenerate = usedCount < limit;
    const percentUsed = limit > 0 ? Math.round((usedCount / limit) * 100) : 0;
    
    return {
      canGenerate,
      usedCount,
      limit,
      percentUsed
    };
  } catch (error: any) {
    console.error('Error checking prompt generation limit:', error);
    return { canGenerate: true, usedCount: 0, limit: 50, percentUsed: 0 };
  }
};

/**
 * Enhanced function to check copilot chat message usage - now uses profiles table
 */
export const canUserSendCopilotMessage = async (userId: string): Promise<{
  canSend: boolean;
  usedCount: number;
  limit: number;
  percentUsed: number;
}> => {
  try {
    if (!featureFlags.enableSubscriptionLimits) {
      return { canSend: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    if (!userId) {
      return { canSend: false, usedCount: 0, limit: 5, percentUsed: 0 };
    }
    
    const admin = await isUserAdmin(userId);
    if (admin) {
      return { canSend: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    const limits = await getAIUsageLimits(userId);
    
    // Get current month usage from ai_chat_usage table
    const { data: periodData } = await supabase.rpc('get_current_billing_period');
    const currentPeriod = periodData?.[0];
    
    if (!currentPeriod) {
      return { canSend: true, usedCount: 0, limit: limits.copilotMessagesLimit, percentUsed: 0 };
    }
    
    const { data: usageData, error } = await supabase
      .from('ai_chat_usage')
      .select('copilot_messages_used')
      .eq('user_id', userId)
      .eq('period_start', currentPeriod.period_start)
      .eq('period_end', currentPeriod.period_end)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching copilot chat usage:', error);
    }
    
    const usedCount = usageData?.copilot_messages_used || 0;
    const limit = limits.copilotMessagesLimit;
    const canSend = usedCount < limit;
    const percentUsed = limit > 0 ? Math.round((usedCount / limit) * 100) : 0;
    
    return {
      canSend,
      usedCount,
      limit,
      percentUsed
    };
  } catch (error: any) {
    console.error('Error checking copilot message limit:', error);
    return { canSend: true, usedCount: 0, limit: 5, percentUsed: 0 };
  }
};
